import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@goodday-clone/ui'
import { RotateCcw, ArrowLeft } from 'lucide-react'
import { LeadDetailModal, LeadDetailData } from '@/components/sales/lead-detail-modal'
import { api } from '@/api/client'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { useLeadStatuses } from '@/shared/hooks/useCrmVocabulary'
import { LeadsSubNav } from '@/features/leads/LeadsSubNav'

export default function LeadsArchivePage() {
  const navigate = useNavigate()
  const { data: leadStatuses = [] } = useLeadStatuses()
  const lostStatusNames = leadStatuses.filter((st) => st.isLost).map((st) => st.name)
  const [archivedLeads, setArchivedLeads] = useState<LeadDetailData[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedLead, setSelectedLead] = useState<LeadDetailData | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)

  const fetchArchivedLeads = async () => {
    setLoading(true)
    try {
      const data = await api.get<any>('/leads')
      const allLeads = Array.isArray(data) ? data : (data?.data || data?.leads || [])
      // Driven by the isLost flag on configured lead statuses, so adding a
      // new closed-lost status doesn't need a code change here.
      const lost = allLeads.filter((l: any) => lostStatusNames.includes(l.status))
      if (lost.length > 0) {
        setArchivedLeads(
          lost.map((l: any, i: number) => ({
            id: l._id || l.id,
            name: l.name || l.contactPerson || 'Lead',
            role: l.role || 'Procurement Lead',
            company: l.company || 'Enterprise',
            contactPerson: l.contactPerson || l.name,
            email: l.email || '',
            phone: l.phone || '',
            assignee: l.assignee || 'Unassigned',
            source: l.source || 'Website',
            status: l.status || 'Unqualified',
            notes: l.notes || 'Archived / Unqualified',
            mapsUrl: l.mapsUrl || '',
            value: l.value ? (typeof l.value === 'number' ? `₹${l.value.toLocaleString()}` : (typeof l.value === 'string' && l.value.startsWith('$') ? l.value.replace('$', '₹') : l.value)) : '₹15,000',
            createdAt: l.createdAt ? new Date(l.createdAt).toLocaleDateString() : '3 weeks ago',
            interest: l.interest || 'Cold',
            rating: typeof l.rating === 'number' ? l.rating : (l.rating ? parseFloat(l.rating) : null),
            number: `#${String(i + 1).padStart(2, '0')}`,
            organizationId: l.organizationId,
            convertedDealId: l.convertedDealId,
          }))
        )
      } else {
        setArchivedLeads([])
      }
    } catch {
      setArchivedLeads([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // leadStatuses loads asynchronously; re-run once it's actually in so
    // lostStatusNames isn't permanently stuck at [] from the first render.
    fetchArchivedLeads()
  }, [leadStatuses])

  const handleRowClick = (lead: LeadDetailData) => {
    setSelectedLead(lead)
    setIsDetailOpen(true)
  }

  const handleUpdate = (updated: LeadDetailData) => {
    setArchivedLeads((prev) =>
      prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l))
    )
  }

  const handleRestore = async (e: React.MouseEvent, item: LeadDetailData) => {
    e.stopPropagation()
    try {
      await api.patch(`/leads/${item.id}`, { status: 'New' }, { skipErrorToast: true })
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'restore this lead'))
      return
    }
    setArchivedLeads((prev) => prev.filter((l) => l.id !== item.id))
    toast.success(`Restored "${item.name}" to active leads!`)
  }

  return (
    <div className="space-y-6 max-w-full mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Leads Archive</h1>
            <p className="text-sm text-muted-foreground">Historical records of disqualified, dormant or postponed leads</p>
          </div>
        </div>
      </div>

      <LeadsSubNav current="archive" />

      <Card>
        <CardHeader className="py-4 px-6 border-b border-border/50 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold">Archived Records ({archivedLeads.length})</CardTitle>
          <Badge variant="secondary" className="text-xs">Archive</Badge>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50">
                <tr>
                  <th className="px-6 py-3">Lead Name</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Reason / Notes</th>
                  <th className="px-4 py-3">Past Value</th>
                  <th className="px-4 py-3">Archived Date</th>
                  <th className="px-6 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">Loading archive...</td>
                  </tr>
                ) : archivedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">No archived leads found.</td>
                  </tr>
                ) : (
                  archivedLeads.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() => handleRowClick(item)}
                      className="hover:bg-muted/40 cursor-pointer transition-colors opacity-85 group"
                    >
                      <td className="px-6 py-3.5">
                        <p className="font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-2">
                          <span>{item.name}</span>
                          {item.number && (
                            <span className="text-[10px] font-mono font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              {item.number}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{item.email}</p>
                      </td>
                      <td className="px-4 py-3.5 font-medium text-foreground">{item.company}</td>
                      <td className="px-4 py-3.5 text-xs text-destructive/80 font-medium">{item.notes}</td>
                      <td className="px-4 py-3.5 text-xs text-muted-foreground line-through">{item.value}</td>
                      <td className="px-4 py-3.5 text-xs text-muted-foreground">{item.createdAt}</td>
                      <td className="px-6 py-3.5 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          onClick={(e) => handleRestore(e, item)}
                        >
                          <RotateCcw className="h-3 w-3" /> Restore
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <LeadDetailModal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        lead={selectedLead}
        onUpdate={handleUpdate}
        onDeleted={(id) => setArchivedLeads((prev) => prev.filter((l) => l.id !== id))}
        currentSectionName="Archive"
        currentSectionHref="/app/leads/archive"
      />
    </div>
  )
}
